import Phaser from 'phaser';
import {
  StateMachine,
  oppositeDirection,
  Direction,
  randomChoice,
  directionalVelocity,
  randomRange,
  getOrientation,
  Orientation,
} from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import Enemy, { EnemyState } from 'tower/scenes/battle/enemy';
import gameState from 'tower/state/game-state';
import Projectile, { ProjectileKey } from './projectile';
import { HitOptions } from 'tower/types';

const MOVE_SPEED = 100;

interface GhostOptions {
  orientation?: 'vertical' | 'horizontal';
}

interface Variant {
  leftFrame: number;
  upFrame: number;
  hp: number;
  projectile: ProjectileKey;
}

const VARIANTS: Record<string, Variant> = {
  blue: {
    leftFrame: 128,
    upFrame: 129,
    hp: 2,
    projectile: 'magic',
  },
  red: {
    leftFrame: 130,
    upFrame: 131,
    hp: 4,
    projectile: 'wallMagic',
  },
};

export default class Ghost extends Enemy {
  needsTurn = false;

  constructor(
    scene: BattleScene,
    x: number,
    y: number,
    public variantType = 'blue',
    public options: GhostOptions = {},
  ) {
    const variant = VARIANTS[variantType];
    const vulnerable = gameState.hasItem('bookOfTruth');
    super(scene, x, y, 'enemies', variant.leftFrame, 'ghost', {
      hp: variant.hp,
      knockback: false,
      passthroughWalls: true,
      hideSpawn: !vulnerable,
      stunDamage: false,
      stunnable: true,
      passthroughDoors: true,
    });
    this.body.setSize(12, 12);

    if (!vulnerable) {
      this.invulnerable = true;
      this.setVisible(false);
    }
  }

  makeStateMachine(scene: BattleScene) {
    return new StateMachine('initial', {
      initial: new GhostInitialState(scene, this),
      moving: new GhostMovingState(scene, this),
      rebound: new GhostReboundState(scene, this),
    });
  }

  moveTowards(direction: Direction) {
    this.direction = direction;
    this.body.setVelocity(...directionalVelocity(MOVE_SPEED, direction));
  }

  hit(options: HitOptions) {
    this.needsTurn = true;
    return super.hit(options);
  }

  get direction() {
    return super.direction;
  }

  set direction(value: Direction) {
    super.direction = value;
    if (!this.variant) {
      return;
    }

    switch (value) {
      case Direction.Up:
        this.setFrame(this.variant.upFrame).setFlipX(false);
        return;
      case Direction.Right:
        this.setFrame(this.variant.leftFrame).setFlipX(true);
        return;
      case Direction.Down:
      case Direction.Left:
        this.setFrame(this.variant.leftFrame).setFlipX(false);
        return;
    }
  }

  get variant() {
    return VARIANTS[this.variantType];
  }
}

class GhostState extends EnemyState {
  declare enemy: Ghost;
}

/** Starts movement after spawn animation */
class GhostInitialState extends GhostState {
  handleEntered() {
    const { enemy } = this;
    const direction = randomChoice(
      enemy.options.orientation === 'horizontal' ? [Direction.Left, Direction.Right] : [Direction.Up, Direction.Down],
    );
    enemy.moveTowards(direction);
    return this.transition('moving');
  }
}

class GhostMovingState extends GhostState {
  nextSpell!: number;

  init() {
    this.nextSpell = this.spellDelay();
  }

  handleEntered() {
    this.enemy.moveTowards(this.enemy.direction);
  }

  update() {
    const { enemy, scene } = this;
    if (!Phaser.Geom.Rectangle.Contains(scene.currentRoom.rect, enemy.x, enemy.y)) {
      const newDirection = oppositeDirection(enemy.direction);
      enemy.moveTowards(newDirection);
      return this.transition('rebound');
    }

    // If we're turning, wait until we're near the nearest space to turn before turning
    if (enemy.needsTurn) {
      const [sx, sy] = scene.currentRoom.maze.worldXYToSpaceXY(enemy.x, enemy.y);
      const [wx, wy] = scene.currentRoom.maze.spaceXYToWorldXY(sx, sy);
      if (Math.abs(enemy.x - wx) < 2 && Math.abs(enemy.y - wy) < 2) {
        let newDirection: Direction;
        if (getOrientation(enemy.direction) === Orientation.Horizontal) {
          newDirection = randomChoice([Direction.Up, Direction.Down]);
        } else {
          newDirection = randomChoice([Direction.Left, Direction.Right]);
        }

        enemy.body.reset(wx, wy);
        enemy.moveTowards(newDirection);
        enemy.needsTurn = false;
      }
    }

    if (--this.nextSpell < 1) {
      new Projectile(scene, enemy.x, enemy.y, enemy.variant.projectile, enemy.direction, { target: 'player' });
      scene.sound.play('magic');
      this.nextSpell = this.spellDelay();
    }
  }

  spellDelay() {
    return randomRange(100, 300);
  }
}

class GhostReboundState extends GhostState {
  update() {
    const { enemy, scene } = this;
    if (Phaser.Geom.Rectangle.Contains(scene.currentRoom.rect, enemy.x, enemy.y)) {
      return this.transition('moving');
    }
  }
}
